import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { GroupeImpayes, Partage } from 'src/app/core/models/finance.model';
import { ParametresFerme } from 'src/app/core/models/parametres.model';
import { ParametresService } from 'src/app/core/services/entities/parametres.service';
import { PaiementService } from 'src/app/core/services/entities/paiement.service';
import { FermeStateService } from 'src/app/core/services/entities/ferme-state.service';
import { FinanceService } from 'src/app/core/services/entities/finance.service';
import { PhoneUtil, remplirModele } from './phone.util';

const f = (n: number | null | undefined) => Math.round(n || 0).toLocaleString('fr-FR').replace(/ | /g, ' ');
const d = (iso?: string | null) => { if (!iso) return ''; const [a, m, j] = iso.slice(0, 10).split('-'); return `${j}/${m}/${a}`; };

/** Messages pré-remplis (modèles des paramètres) et envoi WhatsApp journalisé */
@Injectable({ providedIn: 'root' })
export class MessagesService {

  constructor(private parametres: ParametresService, private paiements: PaiementService, private fermeState: FermeStateService,
              private finances: FinanceService) {}

  private contexte(): Observable<{ p: ParametresFerme | null; ferme: string; tel: string }> {
    return this.parametres.get().pipe(
      catchError(() => of(null)),
      map(p => ({ p, ferme: p?.raisonSociale || this.fermeState.snapshot?.nom || 'la ferme', tel: this.fermeState.snapshot?.telephone || '' }))
    );
  }

  relance(g: GroupeImpayes): Observable<string> {
    return this.contexte().pipe(map(({ p, ferme, tel }) => remplirModele(p?.messageRelance || MODELE_RELANCE, {
      client: g.contactNom || '', ferme, telephone_ferme: tel, reste: f(g.total),
      lignes: g.lignes.map(l => `• ${l.factureNumero ? 'Facture ' + l.factureNumero + ' — ' : ''}${d(l.date)} : ${f(l.reste)} FCFA`
        + (l.dateEcheance ? ` (échéance ${d(l.dateEcheance)})` : '') + (l.factureLien ? `\n  ${l.factureLien}` : '')).join('\n'),
    })));
  }

  recu(v: { client?: string; montant: number; date?: string; mode?: string; objet?: string; reste?: number; lien?: string }): Observable<string> {
    return this.contexte().pipe(map(({ p, ferme }) => remplirModele(p?.messageRecu || MODELE_RECU, {
      client: v.client || '', ferme, montant: f(v.montant), date: d(v.date), mode: v.mode || '', objet: v.objet || 'votre achat',
      reste_phrase: v.reste ? `\nReste dû : ${f(v.reste)} FCFA.` : '\nVous êtes à jour, merci !',
      lien: v.lien || '',
    }).replace(/\n\nReçu : *$/m, '').replace(/Reçu : \n/, '')));
  }

  facture(v: { client?: string; type: 'FACTURE' | 'PROFORMA' | 'AVOIR'; numero: string; montant: number; reste?: number;
               echeance?: string; lien?: string }): Observable<string> {
    const doc = v.type === 'PROFORMA' ? 'facture proforma' : v.type === 'AVOIR' ? 'avoir' : 'facture';
    return this.contexte().pipe(map(({ p, ferme, tel }) => remplirModele(p?.messageFacture || MODELE_FACTURE, {
      client: v.client || '', ferme, telephone_ferme: tel, document: doc, numero: v.numero,
      montant: f(v.montant), reste: f(v.reste ?? v.montant), echeance: d(v.echeance),
      echeance_phrase: v.echeance && v.type === 'FACTURE' ? `, à régler avant le ${d(v.echeance)}` : '',
      lien: v.lien || '',
    }).replace(/\n\nOuvrir le document : *\n/, '\n\n').replace(/Ouvrir le document : *$/m, '').trim()));
  }

  /**
   * Reçu d'une vente envoyé sur WhatsApp : PDF archivé dans Documents › Reçus puis lien partagé.
   * La fenêtre est ouverte tout de suite (geste de l'utilisateur), puis dirigée vers WhatsApp.
   */
  envoyerRecuVente(v: { id: number; contactId?: number; contactNom?: string; montant: number; reste?: number; date?: string;
                        modePaiement?: string; description?: string }, telephone?: string): void {
    const fenetre = window.open('', '_blank');
    const envoyer = (lien?: string) => this.recu({ client: v.contactNom, montant: v.montant - (v.reste || 0), date: v.date,
      mode: v.modePaiement, objet: v.description, reste: v.reste, lien }).subscribe(txt => {
        const url = PhoneUtil.lienWhatsApp(telephone, txt);
        if (fenetre) fenetre.location.href = url; else window.open(url, '_blank', 'noopener');
        this.paiements.journaliser({ type: 'RECU', canal: 'WHATSAPP', fermeId: this.fermeState.fermeId, contactId: v.contactId,
          libelle: v.description, destinataire: PhoneUtil.e164(telephone) || undefined } as Partage).subscribe({ error: () => {} });
      });
    this.finances.archiverRecuVente(v.id).subscribe({ next: r => envoyer(r.lienPublic), error: () => envoyer() });
  }

  /** Ouvre WhatsApp (numéro connu ou choix du destinataire) et journalise l'envoi */
  whatsapp(tel: string | null | undefined, texte: string, journal?: Omit<Partage, 'fermeId' | 'canal'>): void {
    window.open(PhoneUtil.lienWhatsApp(tel, texte), '_blank', 'noopener');
    if (journal) {
      this.paiements.journaliser({ ...journal, fermeId: this.fermeState.fermeId, canal: 'WHATSAPP',
        destinataire: PhoneUtil.e164(tel) || undefined } as Partage).subscribe({ error: () => {} });
    }
  }
}

const MODELE_FACTURE = 'Bonjour {client},\n\nVoici votre {document} n° {numero} de {ferme} : {montant} FCFA{echeance_phrase}.\n\nOuvrir le document : {lien}\n\nMerci de votre confiance.';
const MODELE_RELANCE = 'Bonjour {client},\n\nSauf erreur de notre part, il reste {reste} FCFA à régler :\n{lignes}\n\nMerci de nous indiquer la date de règlement.\n{ferme} · {telephone_ferme}';
const MODELE_RECU = 'Bonjour {client},\n\nNous confirmons la réception de {montant} FCFA le {date} ({mode}) pour {objet}.{reste_phrase}\n\nMerci, {ferme}.';
